// App — router shell. Hosts the header (with route-aware nav) above the
// active route's content. `/` renders MarketView; `/client/...` renders the
// real Client View. All routes are gated by the auth layer.

import React, { useState } from "react";
import { BrowserRouter, Route, Routes, NavLink, useLocation, useParams, useSearchParams, Navigate } from "react-router-dom";
import MarketView from "./MarketView";
import ClientView from "./components/dashboard/client/ClientView";
import Workbench from "./Workbench";
import useHealth from "./hooks/useHealth";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { RequireAuth, RequireView, RequireClient, AccessDenied } from "./auth/Guards";
import Login from "./auth/Login";
import V25Shell from "./marketv2/Shell";
import V25Home from "./marketv2/Home";
import PatternCanonical from "./marketv2/PatternCanonical";
import V25ZoneBrief from "./marketv2/ZoneBrief";
import V25MarketLanding from "./marketv2/MarketLanding";
import V25UseCaseDetail from "./marketv2/UseCaseDetail";
import V25Weekly from "./marketv2/Weekly";
import V25Companies from "./marketv2/Companies";
import V25CompanyDetail from "./marketv2/CompanyDetail";
import V25EvidenceExplorer from "./marketv2/EvidenceExplorer";

/** Preview-environment detection — production domain stays out of the
 *  workbench link entirely. The check is intentionally string-based so
 *  no env flag is required; production is the only hostname we hide on. */
const isPreviewEnv = () => {
    try {
        const host = (typeof window !== "undefined" && window.location && window.location.hostname) || "";
        return !host.includes("lodestar.zuehlke.com");
    } catch { return true; }
};

export const FreshnessPill = ({ health }) => {
    const [open, setOpen] = useState(false);
    if (!health) return null;
    const hasWarning = !!(health.warning || health.lastError);
    const ts = health.generatedAt || health.fetchedAt;

    const ago = (() => {
        if (!ts) return "—";
        const diff = Math.max(0, Date.now() - new Date(ts).getTime());
        const m = Math.floor(diff / 60000);
        if (m < 1) return "just now";
        if (m < 60) return `${m} min ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h} h ago`;
        const d = Math.floor(h / 24);
        return `${d} d ago`;
    })();

    const yw = health.latestIndexedWeek?.yearWeek || health.latestYearWeek || null;
    const tone = hasWarning
        ? "border-[hsl(var(--warning))] text-[hsl(var(--warning))] bg-[hsl(var(--warning-soft))]"
        : "border-[hsl(var(--line))] text-[hsl(var(--ink-soft))] bg-[hsl(var(--surface-raised))] hover:border-[hsl(var(--accent))]";

    return (
        <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
            <button
                type="button"
                data-testid="data-freshness-pill"
                onClick={() => setOpen((o) => !o)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-medium rounded-full border transition-colors num ${tone}`}
            >
                <span aria-hidden className={`inline-block w-1.5 h-1.5 rounded-full ${hasWarning ? "bg-[hsl(var(--warning))]" : "bg-[hsl(var(--positive))]"}`} />
                <span>Updated {ago}</span>
                {yw && (<><span className="text-[hsl(var(--mute))]">·</span><span>Latest {yw}</span></>)}
            </button>
            {open && (health.warning || health.lastError) && (
                <div className="absolute right-0 top-full mt-2 z-30 w-[300px] card-flat p-3 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18)]">
                    <div className="eyebrow mb-1.5">Data status</div>
                    {health.warning && <p className="text-[11px] text-[hsl(var(--warning))] leading-snug">{health.warning}</p>}
                    {health.lastError && <p className="text-[11px] text-[hsl(var(--negative))] leading-snug mt-1">{health.lastError}</p>}
                </div>
            )}
        </div>
    );
};

const Row = ({ k, v }) => (
    <div className="flex justify-between gap-4">
        <dt className="text-[hsl(var(--mute))]">{k}</dt>
        <dd className="text-[hsl(var(--ink))] num text-right break-all">{v}</dd>
    </div>
);

const fmtTs = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
};

export const BrandMark = () => {
    const [imgOk, setImgOk] = useState(true);
    if (!imgOk) return <span className="brand-mark text-base">Zühlke</span>;
    return (
        <img
            src="/brand/zuhlke-logo.png"
            alt="Zühlke"
            data-testid="brand-logo"
            onError={() => setImgOk(false)}
            className="block h-16 w-16 select-none rounded-[3px]"
            draggable={false}
        />
    );
};

const Nav = () => {
    const location = useLocation();
    const { canMarket, canClient, isAdmin } = useAuth();
    const onClientRoute = location.pathname.startsWith("/client");
    const onWorkbenchRoute = location.pathname.startsWith("/workbench");
    const onV1Route = location.pathname.startsWith("/v1");
    const showWorkbench = isAdmin && isPreviewEnv();
    const linkCls = (active) => `px-4 py-2 text-sm font-medium transition-colors ${active ? "bg-[hsl(var(--ink))] text-white" : "bg-[hsl(var(--surface))] text-[hsl(var(--ink-soft))] hover:bg-[hsl(var(--surface-sunken))]"}`;
    // Hide the toggle entirely when the user only has access to one view
    // AND the workbench link is also hidden. Otherwise we still want to
    // surface the workbench preview for admin users.
    if (!canMarket || !canClient) {
        if (!showWorkbench) return null;
    }
    return (
        <div className="flex border border-[hsl(var(--line))] rounded-sm overflow-hidden" role="tablist">
            {canMarket && (
                <NavLink to="/v1" data-testid="view-market" role="tab" aria-selected={onV1Route} className={() => linkCls(onV1Route)}>Market view</NavLink>
            )}
            {canClient && (
                <NavLink to="/client" data-testid="view-client" role="tab" aria-selected={onClientRoute} className={() => linkCls(onClientRoute)}>Client view</NavLink>
            )}
            {showWorkbench && (
                <NavLink to="/workbench" data-testid="view-workbench" role="tab" aria-selected={onWorkbenchRoute} className={() => linkCls(onWorkbenchRoute)} title="Preview only — admin-restricted">
                    Workbench<span className="ml-1.5 text-[9px] uppercase tracking-wider opacity-70">preview</span>
                </NavLink>
            )}
        </div>
    );
};

const UserChip = () => {
    const { user, logout } = useAuth();
    const [busy, setBusy] = useState(false);
    if (!user) return null;
    const onLogout = async () => {
        if (busy) return;
        setBusy(true);
        await logout();
        setBusy(false);
    };
    return (
        <div className="flex items-center gap-2" data-testid="user-chip">
            <NavLink
                to="/market-v2"
                data-testid="back-to-lodestar-link"
                className="text-[11px] font-medium text-[hsl(var(--accent-ink))] hover:underline mr-2"
                title="Back to the main Lodestar workspace"
            >← Back to Lodestar</NavLink>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-medium rounded-full border border-[hsl(var(--line))] bg-[hsl(var(--surface-raised))] text-[hsl(var(--ink-soft))]">
                <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))]" />
                <span data-testid="user-display-name">{user.displayName || user.username}</span>
                <span className="text-[hsl(var(--mute))]">·</span>
                <span className="uppercase tracking-wider text-[10px]" data-testid="user-role">{user.role}</span>
            </span>
            <button
                type="button"
                onClick={onLogout}
                disabled={busy}
                data-testid="logout-button"
                className="text-[11px] font-medium text-[hsl(var(--mute))] hover:text-[hsl(var(--ink))] transition-colors px-2 py-1"
            >
                {busy ? "…" : "Sign out"}
            </button>
        </div>
    );
};

const Header = ({ health }) => (
    <header className="relative z-10 border-b border-[hsl(var(--line))] bg-[hsl(var(--surface-raised))]">
        <div className="max-w-[1600px] mx-auto px-8 py-6">
            <div className="flex items-start justify-between gap-8 flex-wrap">
                <div className="flex items-start gap-5">
                    <BrandMark />
                    <div className="border-l border-[hsl(var(--line))] pl-5">
                        <div className="eyebrow mb-1.5">Market intelligence</div>
                        <h1 className="h-product text-[26px]">Digital Assets Lodestar</h1>
                        <p className="text-sm text-[hsl(var(--mute))] mt-1.5 max-w-xl leading-relaxed">
                            Extracting signals from the noise.
                        </p>
                    </div>
                </div>
                <div className="flex flex-col items-end gap-3">
                    <div className="flex items-center gap-3">
                        <UserChip />
                        <FreshnessPill health={health} />
                    </div>
                    <Nav />
                </div>
            </div>
        </div>
    </header>
);

const Shell = ({ children }) => {
    const health = useHealth();
    return (
        <div className="min-h-screen page-grain">
            <Header health={health} />
            <main className="max-w-[1600px] mx-auto px-8 py-6 space-y-6 relative z-10">
                {children}
            </main>
        </div>
    );
};

const WorkbenchRoute = () => {
    const { isAdmin } = useAuth();
    if (!isAdmin || !isPreviewEnv()) return <AccessDenied />;
    return <Workbench />;
};

const ClientRouteGuard = () => {
    const { clientId } = useParams();
    return (
        <RequireClient clientId={clientId}>
            <ClientView />
        </RequireClient>
    );
};

/** Canonical `cpat_…` ids render the authoritative pattern card; legacy POC ids
 *  fall back to the authoritative Market landing (legacy detail views retired). */
const PatternRedirect = () => {
    const { candidateId } = useParams();
    if ((candidateId || "").startsWith("cpat_")) return <PatternCanonical />;
    return <Navigate to="/market-v2/market" replace />;
};

/** WP13: ?zone={SCOPE_KEY} renders the authoritative Market Zone brief; otherwise the authoritative Market landing. */
const MarketZoneRoute = () => {
    const [params] = useSearchParams();
    const zone = params.get("zone") || "";
    if (/^[A-Z][A-Z_]*$/.test(zone)) return <V25ZoneBrief />;
    return <V25MarketLanding />;
};

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route element={<RequireAuth />}>
                        {/* v2.5 is the default experience — `/` enters the workspace. */}
                        <Route path="/" element={<Navigate to="/market-v2" replace />} />
                        {/* Lodestar v1 — intact fallback/backup experience. */}
                        <Route
                            path="/v1"
                            element={
                                <Shell>
                                    <RequireView view="market"><MarketView /></RequireView>
                                </Shell>
                            }
                        />
                        <Route
                            path="/client"
                            element={
                                <Shell>
                                    <RequireView view="client"><ClientView /></RequireView>
                                </Shell>
                            }
                        />
                        <Route
                            path="/client/:clientId"
                            element={
                                <Shell>
                                    <RequireView view="client"><ClientRouteGuard /></RequireView>
                                </Shell>
                            }
                        />
                        <Route
                            path="/client/:clientId/:yearWeek"
                            element={
                                <Shell>
                                    <RequireView view="client"><ClientRouteGuard /></RequireView>
                                </Shell>
                            }
                        />
                        <Route
                            path="/workbench"
                            element={
                                <Shell>
                                    <WorkbenchRoute />
                                </Shell>
                            }
                        />
                        {/* v2.5 workspace — default product experience (v1 preserved at /v1). */}
                        <Route path="/market-v2" element={<RequireView view="market"><V25Shell /></RequireView>}>
                            <Route index element={<V25Home />} />
                            {/* Primary Market Structure workspace (tree + contextual pane). */}
                            <Route path="market" element={<MarketZoneRoute />} />
                            <Route path="market/use-cases/:useCaseKey" element={<V25UseCaseDetail />} />
                            <Route path="market/:candidateId" element={<PatternRedirect />} />
                            {/* Backwards-compat redirects for the previous /patterns and /entities routes. */}
                            <Route path="patterns" element={<Navigate to="/market-v2/market" replace />} />
                            <Route path="patterns/:candidateId" element={<PatternRedirect />} />
                            <Route path="weekly" element={<V25Weekly />} />
                            <Route path="organizations" element={<V25Companies />} />
                            <Route path="organizations/:corgId" element={<V25CompanyDetail />} />
                            {/* Legacy POC entity routes → canonical Companies experience. */}
                            <Route path="organizations/:kind/:name" element={<Navigate to="/market-v2/organizations" replace />} />
                            <Route path="entities" element={<Navigate to="/market-v2/organizations" replace />} />
                            <Route path="entities/:kind/:name" element={<Navigate to="/market-v2/organizations" replace />} />
                            <Route path="evidence" element={<V25EvidenceExplorer />} />
                        </Route>
                        <Route path="*" element={<Navigate to="/market-v2" replace />} />
                    </Route>
                </Routes>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
